import { useState, useEffect, useContext, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link} from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong, faDownload, faRobot } from '@fortawesome/free-solid-svg-icons';
import ReactPlayer from 'react-player';
import { Config } from '../../config/Config';
import { useAiImageTypesGeneration } from '../../hooks/useAiImageTypesGeneration';
import { stateIconDone, stateIconError, stateIconWaiting } from '../../types/ai/states';
import { downloadOriginal } from '../../utils/exportMedia';
import { AuthedVideoPlayer } from '../../components/ui/AuthedVideoPlayer';
import { ApiPayment } from '../../apis/ApiPayment';

interface GernerationProps {
    'src': string[],
    'id':number,
    'promptPositive':string,
    'promptNegative':string
};

const exampleData: GernerationProps[] = [
    {
        id:1,
        src:['https://www.youtube.com/watch?v=MypFGo7p1DQ'],
        promptPositive:'Peaceful sunset over a calm lake, reflection in the water, warm golden hour colors, soft light rays, serene atmosphere, photorealistic, high detail',
        promptNegative: 'no people, no boats, no fog, no oversaturated colors, no artificial colors, no blurriness, no noise'
    },
    {
        id:2,
        src:['https://www.youtube.com/watch?v=3sbu7LQeuiw'],
        promptPositive:'Portrait of an elegant woman in the style of Alfons Mucha, floral ornaments, flowing hair, pastel tones, soft lighting, detailed line work, artistic',
        promptNegative: 'no modern elements, no photography, no harsh shadows, no dark or gloomy mood, no digital artifacts'
    },
    {
        id:3,
        src:['https://www.youtube.com/shorts/FCoIja9iheI'],
        promptPositive:'Cyberpunk city at night, neon lights, wet streets with reflections, futuristic architecture, dynamic perspective, atmospheric lighting',
        promptNegative:'no crowds, no smog, no blur, no explosions, no old-fashioned cars, no dull gray colors'
    },
    {
        id:4,
        src:['https://www.youtube.com/shorts/D7BGHpO_tNI'],
        promptPositive:'Highly detailed coin design featuring a sengi (elephant shrew), engraved style, intricate patterns, realistic metallic texture, elegant ornamental borders, shiny gold or silver surface',
        promptNegative: 'no other animals, no background scenery, no cartoonish style, no color noise, no scratches, no unrealistic textures'
    },
    {
        id:5,
        src:['https://www.youtube.com/shorts/SzQY6nFwtGE','https://www.youtube.com/watch?v=3sbu7LQeuiw','https://www.youtube.com/shorts/FCoIja9iheI','https://www.youtube.com/shorts/D7BGHpO_tNI'],
        promptPositive:'Peaceful sunset over a calm lake, reflection in the water, warm golden hour colors, soft light rays, serene atmosphere, photorealistic, high detail',
        promptNegative: 'no people, no boats, no fog, no oversaturated colors, no artificial colors, no blurriness, no noise'
    }
];
export const VideoGeneration = () => {
    const { t } = useTranslation('generation');
    const [generatedVideos,setGeneratedImages] = useState<GernerationProps[]>([]);
    const [inputs, setInputs] = useState<{'promptPositive':string,'promptNegative':string,'seed':number}>({
        promptNegative:'',
        promptPositive: '',
        seed: 42
    });
    const [generation,state,setState,getJobID,insufficientCreditsDialog] = useAiImageTypesGeneration();
    const [creationCosts, setCreationCosts] = useState<number|null>(0);

    // video_gen is a fixed price (no width/height/duration params yet), so estimate once on mount
    // instead of on every input change - see ApiPayment.GetCostsForVideoCreate.
    const updateCosts = async() => {
        try {
            const response = await ApiPayment.GetCostsForVideoCreate();
            setCreationCosts(response.costs);
        } catch {
            console.log('Error in calculation creation costs.');
        }
    };

    const handleGenerationResult = () => {
        let videos = generation;
        //add image to history list
        const newGeneratedVideos:GernerationProps[] = [...generatedVideos];
        let lastID = newGeneratedVideos.length > 0 ? newGeneratedVideos[newGeneratedVideos.length-1].id : 0;
        newGeneratedVideos.push({
            'id':lastID+1,
            'promptPositive': inputs.promptPositive,
            'promptNegative': inputs.promptNegative,
            'src': videos
        });
        setGeneratedImages(newGeneratedVideos);
    };

    const generateVideo = async() => {
        setState(null);
        //request to get job id
        const body = { positive_prompt: inputs.promptPositive, negative_prompt: inputs.promptNegative};
        setState('send');
        let path = 'generate/video'
        getJobID('video_gen',path,body)
    };
    const handleInputChange = (event:ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        const name = event.target.name;
        setInputs(prev => ({...prev,[name]: value}));
    };
    
    const downloadVideo = async (videoID:number) => {
        const videoObjekt: GernerationProps | undefined = generatedVideos.find(item => item.id === videoID);
        if(videoObjekt === undefined){return;}
        for(let videoUrl of videoObjekt.src){
              try {
                const fileName = videoUrl.replaceAll('\\','/').split('/').slice(-1)[0];
                await downloadOriginal(videoUrl, fileName);
            } catch (err) {
                console.error('Download fehlgeschlagen:', err);
            }
        };
    };

    useEffect(()=>{
        if(generation.length > 0){
            handleGenerationResult();
        }
    },[generation]);
    useEffect(()=>{
        updateCosts();
    },[]);

  return (
    <>
        {insufficientCreditsDialog}
        <div className="container-fluid">
            <div className='d-flex flex-column h-100'>
                <div className='row mx-0'>
                    <div className='col-auto p-2 pe-0'>
                        <Link
                            to="/executions"
                            className="btn btn-outline-secondary btn-sm ms-3"
                        >
                        <FontAwesomeIcon
                                icon={faArrowLeftLong}
                                size="1x"
                                title={t('back')}
                            />
                        </Link>
                    </div>
                </div>
                <div className='flex-grow-1 p-3 overflow-auto'>
                {state
                    ? state === 'send'
                        ? <p className='h4'>{t('state.sending')}</p>
                        : state === 'waiting'
                            ? <p className='h4'><span className="spin">{stateIconWaiting}</span> {t('state.waiting')}</p>
                            : state === 'error'
                                ? <p className='h4'>{stateIconError} {t('state.error')}</p>
                                : state === 'done'
                                    ? <p className='h4'>{stateIconDone} {t('state.done')}</p>
                                    : <></>
                    : <></>
                }
                {generatedVideos.slice().sort((a, b) => b.id - a.id).map(item=>(
                    <div className='row p-2 my-1 text-center align-items-center border-bottom border-secondary'>
                        <div className='col-5 d-flex flex-wrap py-auto' style={{gap:'10px'}}>
                            {item.src.map(video => (
                                <div className='text-center my-1' style={{height:'350px', aspectRatio: '10 / 4', paddingLeft:'10%', paddingRight:'10%'}}>
                                    <AuthedVideoPlayer
                                        url={video}
                                        controls={ true}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className='col d-flex flex-column justify-content-center text-start'>
                            <div className='row mb-2'>
                                <p className='fw-bold'>{t('prompt.positiveShort')}</p>
                                <p className='mb-2'>{item.promptPositive}</p>
                                <p className='fw-bold'>{t('prompt.negativeShort')}</p>
                                <p className='mb-2'>{item.promptNegative}</p>
                            </div>
                            <div className='row mb-2'>
                                <div className='col-auto p-0'>
                                    <button
                                        className="btn btn-outline-secondary"
                                        title={t('download.video')}
                                        onClick={() => downloadVideo(item.id)}
                                    >
                                        <FontAwesomeIcon
                                            icon={faDownload}
                                            size='xl'
                                            className='bg-transparent me-2'
                                        />
                                        {t('download.video')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                ))}
                </div>
                <div className='row p-3'>
                    <div className='col'>
                        <label htmlFor="promptPositive" className="form-label">{t('prompt.positive')}</label>
                        <textarea
                            className="form-control"
                            id="promptPositive"
                            name='promptPositive'
                            rows={3}
                            value={inputs.promptPositive}
                            onChange={(e) => handleInputChange(e)}
                            placeholder={t('placeholder.positive')}
                        />
                    </div>
                    <div className='col'>
                        <label htmlFor="promptNegative" className="form-label">{t('prompt.negative')}</label>
                        <textarea
                            className="form-control"
                            id="promptNegative"
                            name='promptNegative'
                            rows={3}
                            value={inputs.promptNegative}
                            onChange={(e) => handleInputChange(e)}
                            placeholder={t('placeholder.negative')}
                        />
                    </div>
                    <div className='col-auto d-flex flex-column justify-content-center text-start'>
                        <button
                            className="btn btn-primary"
                            title={t('generateVideo')}
                            onClick={() => generateVideo()}
                        >
                            <FontAwesomeIcon
                                icon={faRobot}
                                size='xl'
                                className='bg-transparent me-2'
                            />
                            {t('coins')} {creationCosts ?? '—'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </>
  );
};

export default VideoGeneration;